import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ActionKind } from "../../types/Action";
import { OperatorSubStateKind } from "../../types/Operator";
import { useConfig } from "../ConfigProvider";
import { ConfirmChangeDialog } from "../Dialog";
import {
    RootStateKind,
    useRootAndSubState,
    useRootState
} from "../state/StateProvider";
import { ToggleSwitch } from "../ToggleSwitch";

export const Configuration = () => {
    const {
        state: { accessibilityMode },
        dispatch
    } = useRootState(RootStateKind.Operator);

    useRootAndSubState(
        RootStateKind.Operator,
        OperatorSubStateKind.Configuration
    );

    const { t } = useTranslation();
    const { isEnabled, cartCheckEnabled } = useConfig();

    const [showConfirmStatusDialog, setShowConfirmStatusDialog] =
        useState<boolean>(false);

    const [showCartCheckChangeDialog, setShowCartCheckChangeDialog] =
        useState<boolean>(false);

    const ConfirmCartCheckChangeDialog = () => {
        if (!showCartCheckChangeDialog) {
            return null;
        } else {
            const confirmAction = () => {
                dispatch({
                    kind: ActionKind.SetCartCheck,
                    isEnabled: !cartCheckEnabled
                });
                setShowCartCheckChangeDialog(false);
            };

            const rejectAction = () => setShowCartCheckChangeDialog(false);

            const title = cartCheckEnabled
                ? t("operator.configuration.cartCheck.confirmDisable")
                : t("operator.configuration.cartCheck.confirmEnable");

            return (
                <ConfirmChangeDialog
                    title={title}
                    confirmTitle={t("yes")}
                    confirmAction={confirmAction}
                    rejectTitle={t("no")}
                    rejectAction={rejectAction}
                />
            );
        }
    };

    const ConfirmStatusChangeDialog = () => {
        if (!showConfirmStatusDialog) {
            return null;
        } else {
            const confirmAction = () => {
                dispatch({
                    kind: ActionKind.SetScoStatus,
                    isEnabled: !isEnabled
                });
                setShowConfirmStatusDialog(false);
            };

            const rejectAction = () => setShowConfirmStatusDialog(false);

            const title = isEnabled
                ? t("operator.configuration.status.confirmDisable")
                : t("operator.configuration.status.confirmEnable");

            return (
                <ConfirmChangeDialog
                    title={title}
                    confirmTitle={t("yes")}
                    confirmAction={confirmAction}
                    rejectTitle={t("no")}
                    rejectAction={rejectAction}
                />
            );
        }
    };

    return (
        <div className="m-4 flex h-full flex-col gap-4 overflow-y-auto pb-16">
            <ConfirmStatusChangeDialog />
            <ConfirmCartCheckChangeDialog />
            <div
                className={`flex flex-col items-center gap-4 ${
                    accessibilityMode ? "mt-auto" : ""
                }`}
            >
                <div className="flex w-[75%] flex-col gap-4">
                    <div className="text-2xl font-semibold text-primary-900">
                        {t("operator.configuration.individualConfig.title")}
                    </div>
                    <ToggleSwitch
                        dataTestId="toggle-sco"
                        value={isEnabled}
                        labelText={t("operator.configuration.status.label")}
                        onChangeAction={() => setShowConfirmStatusDialog(true)}
                    />
                </div>
                <div className="flex w-[75%] flex-col gap-4 pt-5">
                    <div className="text-2xl font-semibold text-primary-900">
                        {t("operator.configuration.storeConfig.title")}
                    </div>
                    <ToggleSwitch
                        dataTestId="toggle-cart-check"
                        value={cartCheckEnabled}
                        labelText={t("operator.configuration.cartCheck.label")}
                        onChangeAction={() =>
                            setShowCartCheckChangeDialog(true)
                        }
                    />
                </div>
            </div>
        </div>
    );
};
